
import { supabase } from '@/integrations/supabase/client';
import LoggingService from './LoggingService';

export interface SharePointConnectionSettings {
  site_url: string;
  client_id: string;
  tenant_id: string;
  client_secret: string;
  certificate: string;
  certificate_password: string;
  use_spfx_context?: boolean; // Added SPFx context flag
}

export interface GraphConnectionSettings {
  client_id: string;
  tenant_id: string;
  client_secret: string;
  certificate: string;
  certificate_password: string;
  scopes: string;
  endpoint: string;
}

class SupabaseConnectionService {
  private static instance: SupabaseConnectionService;
  
  private constructor() {}
  
  public static getInstance(): SupabaseConnectionService {
    if (!SupabaseConnectionService.instance) {
      SupabaseConnectionService.instance = new SupabaseConnectionService();
    }
    return SupabaseConnectionService.instance;
  }
  
  /**
   * Save SharePoint connection settings
   */
  public async saveSharePointSettings(settings: SharePointConnectionSettings): Promise<void> {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      LoggingService.info('Saving SharePoint settings', {
        userId,
        hasSiteUrl: !!settings.site_url,
        hasClientId: !!settings.client_id,
        useSpfxContext: !!settings.use_spfx_context,
        hasCertificate: !!settings.certificate
      }, 'SupabaseConnectionService');
      
      // Check if settings already exist
      const { data: existingData } = await supabase
        .from('sharepoint_connections')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('sharepoint_connections')
          .update({
            site_url: settings.site_url,
            client_id: settings.client_id,
            tenant_id: settings.tenant_id,
            client_secret: settings.client_secret,
            certificate: settings.certificate,
            certificate_password: settings.certificate_password,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
        
        if (error) throw error;
        
        // Store SPFx context flag separately in app_logs
        if (settings.use_spfx_context !== undefined) {
          await LoggingService.info('SharePoint SPFx context setting', {
            useSpfxContext: settings.use_spfx_context
          }, 'SharePointSettings');
        }
      } else {
        // Insert new record
        const { error } = await supabase
          .from('sharepoint_connections')
          .insert({
            user_id: userId,
            site_url: settings.site_url,
            client_id: settings.client_id,
            tenant_id: settings.tenant_id,
            client_secret: settings.client_secret,
            certificate: settings.certificate,
            certificate_password: settings.certificate_password
          });
        
        if (error) throw error;
        
        // Store SPFx context flag separately in app_logs
        if (settings.use_spfx_context !== undefined) {
          await LoggingService.info('SharePoint SPFx context setting', {
            useSpfxContext: settings.use_spfx_context
          }, 'SharePointSettings');
        }
      }
      
      LoggingService.info('SharePoint settings saved successfully', {}, 'SupabaseConnectionService');
    } catch (error) {
      LoggingService.error('Error saving SharePoint settings', error, 'SupabaseConnectionService');
      throw error;
    }
  }
  
  /**
   * Get SharePoint connection settings
   */
  public async getSharePointSettings(): Promise<SharePointConnectionSettings | null> {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Get SharePoint connection settings
      const { data: spData, error: spError } = await supabase
        .from('sharepoint_connections')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (spError) {
        throw spError;
      }
      
      if (!spData) {
        return null;
      }
      
      // Get SPFx context setting from app_logs
      const { data: spfxData } = await supabase
        .from('app_logs')
        .select('details')
        .eq('source', 'SharePointSettings')
        .eq('user_id', userId)
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      // Extract the useSpfxContext value safely from the JSON object
      let useSpfxContext = false;
      if (spfxData && spfxData.details) {
        // Safely check if details has the useSpfxContext property
        const details = spfxData.details as Record<string, any>;
        useSpfxContext = details.useSpfxContext === true;
      }
      
      // Build full settings object
      const settings: SharePointConnectionSettings = {
        site_url: spData.site_url,
        client_id: spData.client_id,
        tenant_id: spData.tenant_id,
        client_secret: spData.client_secret || '',
        certificate: spData.certificate || '',
        certificate_password: spData.certificate_password || '',
        use_spfx_context: useSpfxContext
      };
      
      return settings;
    } catch (error) {
      LoggingService.error('Error getting SharePoint settings', error, 'SupabaseConnectionService');
      return null;
    }
  }
  
  /**
   * Save Graph API connection settings
   */
  public async saveGraphSettings(settings: GraphConnectionSettings): Promise<void> {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      LoggingService.info('Saving Graph API settings', {
        userId,
        hasClientId: !!settings.client_id,
        hasScopes: !!settings.scopes,
        hasEndpoint: !!settings.endpoint
      }, 'SupabaseConnectionService');
      
      // Check if settings already exist
      const { data: existingData } = await supabase
        .from('graph_connections')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('graph_connections')
          .update({
            client_id: settings.client_id,
            tenant_id: settings.tenant_id,
            client_secret: settings.client_secret,
            certificate: settings.certificate,
            certificate_password: settings.certificate_password,
            scopes: settings.scopes,
            endpoint: settings.endpoint,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('graph_connections')
          .insert({
            user_id: userId,
            client_id: settings.client_id,
            tenant_id: settings.tenant_id,
            client_secret: settings.client_secret,
            certificate: settings.certificate,
            certificate_password: settings.certificate_password,
            scopes: settings.scopes,
            endpoint: settings.endpoint
          });
        
        if (error) throw error;
      }
      
      LoggingService.info('Graph API settings saved successfully', {}, 'SupabaseConnectionService');
    } catch (error) {
      LoggingService.error('Error saving Graph API settings', error, 'SupabaseConnectionService');
      throw error;
    }
  }
  
  /**
   * Get Graph API connection settings
   */
  public async getGraphSettings(): Promise<GraphConnectionSettings | null> {
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const { data, error } = await supabase
        .from('graph_connections')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      const settings: GraphConnectionSettings = {
        client_id: data.client_id,
        tenant_id: data.tenant_id,
        client_secret: data.client_secret || '',
        certificate: data.certificate || '',
        certificate_password: data.certificate_password || '',
        scopes: data.scopes,
        endpoint: data.endpoint
      };
      
      return settings;
    } catch (error) {
      LoggingService.error('Error getting Graph API settings', error, 'SupabaseConnectionService');
      return null;
    }
  }
}

export default SupabaseConnectionService.getInstance();
